import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { getRentalOrdersRedux } from '../../../redux/slices/rentals';
import { useDispatch, useSelector } from '../../../redux/store';
import CustomDataGrid from '../../../reusable-components/datagrid/CustomDataGrid';
import {
  CURRENT_RENTALS,
  PENDING_RENTALS,
  PREVIOUS_RENTALS,
  ALL_RENTAL_ITEMS,
} from '../../../reusable-components/datagrid/rentalColumns';
import ManagerChipsWrapper from '../../../reusable-components/datagrid/ManagerChipsWrapper';
import CustomToolbar from './CustomToolbarRental';
import NewRental from '../../../layouts/dashboard/header/NewRental';
import { NotificationIdsChip, useNotificationIdsFilter } from '../../../hooks/useNotificationIdsFilter';
import { NotificationMetricType } from '../../../redux/slices/notifications';

export default function RentalOrdersView() {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [rows, setRows] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [viewAllItems, setViewAllItems] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { matchesNotificationIds, isNotificationLoading } = useNotificationIdsFilter([NotificationMetricType.RentalsNeedApproval, NotificationMetricType.RentalsSubmitted]);
  const orderStatus = location.pathname.split('/').pop().replace('-rentals', '').toLowerCase();
  const [openRentalDialog, setOpenRentalDialog] = useState(false);
  const [openNewOrderDialog, setOpenNewOrderDialog] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    data: { pendingRentals, currentRentals, previousRentals },
  } = useSelector((state) => state.rentals);

  const fetchData = async () => {
    try {
      setDataLoaded(false); 
      const groupedItems = rentals.reduce((acc, item) => {
        if (acc.some((order) => order.orderId === item.orderId)) {
          return acc.map((order) => (
            order.orderId === item.orderId
              ? {
                ...order,
                total: (order.total || 0) + (item.total || 0),
                orderedQty: (order.orderedQty || 0) + (item.orderedQty || 0),
              }
              : order
          ));
        }

        return [
          ...acc,
          {
            ...item,
            id: item.orderId,
            company: item.companyName,
            submittedBy: item.addedBy,
            submittedOn: item.dateAdded,
            residentDeletedFlag: item.residentDeleteFlag,
            linkedTickets: item.ticketIds || []
          }
        ];
      }, []);

      setRows(groupedItems);
      setDataLoaded(true);
    } catch (error) {
      console.error('Error fetching vendor data:', error);
      setDataLoaded(true);
    }
  };
  useEffect(() => {
    fetchData();
  }, [rentals]);

  useEffect(() => {
    if (
      (orderStatus.toLowerCase() === 'current' && currentRentals?.length === 0) ||
      (orderStatus.toLowerCase() === 'previous' && previousRentals?.length === 0) ||
      (orderStatus.toLowerCase() === 'pending' && pendingRentals?.length === 0)
    ) {
      dispatch(getRentalOrdersRedux(orderStatus.toUpperCase()));
    }
  }, [orderStatus]);

  useEffect(() => {
    setRentals(
      orderStatus.toLowerCase() === 'pending'
        ? pendingRentals
        : orderStatus.toLowerCase() === 'current'
          ? currentRentals
          : previousRentals
    );
  }, [pendingRentals, currentRentals, previousRentals, orderStatus]);

  useEffect(() => {
    if (searchParams.get('view') === 'allItems') {
      setViewAllItems(true);
    }
  }, [searchParams]);

  const CustomLeftToolbar = useCallback(() => {
    if (rows.length === 0) return null;
    return (
      <CustomToolbar
        setOpenNewOrder={setOpenRentalDialog}
        viewAllItems={viewAllItems}
        setViewAllItems={setViewAllItems}
      />
    );
  }, [rows, viewAllItems]);

  const manager = ManagerChipsWrapper(
    orderStatus === 'previous' ? null : () => dispatch(getRentalOrdersRedux(orderStatus)),
    'Rentals',
    () => dispatch(getRentalOrdersRedux(orderStatus)),
    true
  );

  let gridColumns;
  if (viewAllItems) {
    gridColumns = [...ALL_RENTAL_ITEMS];
  } else if (orderStatus === 'pending' && !viewAllItems) {
    gridColumns = [...PENDING_RENTALS, manager];
  } else if (orderStatus === 'current' && !viewAllItems) {
    gridColumns = [...CURRENT_RENTALS, manager];
  } else if (orderStatus === 'previous' && !viewAllItems) {
    gridColumns = [...PREVIOUS_RENTALS, manager];
  }

  const rowClick = (orderId, fromAllItems = false) => {
    navigate(`/dashboard/rentals/rental-order/${orderId}/${orderStatus}${fromAllItems ? '?back=allRentalItems' : ''}`);
  };

  const handleRowClick = useCallback(
    (params, e) => {
      if (e.openTicket || e.deleteChip || e.addChip) return;
      if (viewAllItems && e.clickedImage) return;

      const selection = window.getSelection().toString();
      if (selection.length > 0) return;

      rowClick(params.row.orderId, viewAllItems);
    },
    [viewAllItems, rowClick]
  );

  const [notificationRentals, notificationRows] = useMemo(() => {
    const isNotification = (row) => matchesNotificationIds(row.orderId);
    return [rentals.filter(isNotification), rows.filter(isNotification)];
  }, [matchesNotificationIds, rentals, rows]);

  const gridProps = {
    gridId: viewAllItems ? 'all-rental-items' : 'pending-rentals',
    data: viewAllItems ? notificationRentals : notificationRows,
    getRowId: viewAllItems ? (row) => `${row.orderId}-${row.cartItemId}-${row.itemId}` : (row) => row.orderId,
    sort: viewAllItems ? [{ field: 'orderNumber', sort: 'asc' }] : [{ field: 'name', sort: 'asc' }],
  };

  return (
    <>
      <NotificationIdsChip label="rentals" />
      <CustomDataGrid
        key={gridProps.gridId}
        {...gridProps}
        boxSX={{ width: '100%', height: 'calc(100vh - 260px)' }}
        gridColumns={gridColumns}
        quickfilterHiddenSearchCol={
          viewAllItems
            ? {
                field: 'serialNumber',
                valueGetter: (params) => params.row.serialNumber || '',
              }
            : { field: 'orderSerialNumbers' }
        }
        isLoading={!dataLoaded || isNotificationLoading}
        CustomLeftToolbar={CustomLeftToolbar}
        onRowClick={handleRowClick}
      />
      {openRentalDialog && (
        <NewRental
          openRentalDialog={openRentalDialog}
          setOpenRentalDialog={setOpenRentalDialog}
          setOpenNewOrderDialog={setOpenNewOrderDialog}
          isExistingOrder
        />
      )}
    </>
  );
}
